import { Controller } from "@hotwired/stimulus"

// Detects whether the Ecomdune Chrome extension is installed and logged in
// *in the current browser*. This can only be answered client-side — the
// extension has to respond to a direct message from this page. See
// docs/api.md#browser-side-detection-dashboard--extension for the contract
// the extension must implement.
export default class extends Controller {
  static targets = [ "loading", "linked", "installed", "notInstalled", "unavailable" ]
  static values = { extensionId: String, checkUrl: String }

  connect() {
    this.detect()
  }

  async detect() {
    if (!this.extensionIdValue || !window.chrome?.runtime?.sendMessage) {
      this.show("unavailable")
      return
    }

    const pingResponse = await this.pingWithRetry()
    if (!pingResponse?.installed) {
      this.show("notInstalled")
      return
    }

    if (!pingResponse.installationId) {
      this.show(pingResponse.authLinked ? "linked" : "installed")
      return
    }

    const confirmed = await this.confirm(pingResponse.installationId)
    this.show(confirmed?.linked ? "linked" : "installed")
  }

  // A single ping is prone to false negatives even when the extension is
  // genuinely installed and healthy: MV3 background scripts run as
  // event-driven service workers that go idle and unload, and waking one
  // back up to answer sendMessage isn't instant or bounded -- it can
  // occasionally exceed a single short timeout. Chrome's silent
  // auto-update/reload cycle causes the same failure mode (a brief
  // "receiving end does not exist" window). Retrying a couple of times
  // before concluding "not installed" absorbs both without meaningfully
  // slowing down the common case, where the first attempt succeeds.
  async pingWithRetry(attempts = 3, delayMs = 400) {
    for (let attempt = 1; attempt <= attempts; attempt++) {
      const response = await this.ping()
      if (response) return response
      if (attempt < attempts) await this.sleep(delayMs)
    }
    return null
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  ping() {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => resolve(null), 1500)

      chrome.runtime.sendMessage(this.extensionIdValue, { type: "ping" }, (response) => {
        clearTimeout(timeout)
        resolve(chrome.runtime.lastError ? null : response)
      })
    })
  }

  async confirm(installationId) {
    try {
      const url = `${this.checkUrlValue}?installation_id=${encodeURIComponent(installationId)}`
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      return response.ok ? await response.json() : null
    } catch {
      return null
    }
  }

  show(state) {
    const panels = {
      linked: this.linkedTarget,
      installed: this.installedTarget,
      notInstalled: this.notInstalledTarget,
      unavailable: this.unavailableTarget
    }

    this.loadingTarget.classList.add("d-none")
    Object.entries(panels).forEach(([key, el]) => el.classList.toggle("d-none", key !== state))
  }
}
